import type { jsPDF } from 'jspdf';
import { sortBy } from 'ramda';
import type { Editor, NodeEntry } from 'slate';

import type { PDFLinks } from './links';
import { PDFRenderer } from './pdf-renderer';
import { nullRenderOptions, StaticRenderer } from '../../../rendering';
import type { NodeBoundingBox, RenderModelPage } from '../../../slate-api';
import { Queries } from '../../../slate-api';
import type { ImageMap } from '../../../types';

export function createPDFPage(
  editor: Editor,
  pdf: jsPDF,
  rMPageEntry: NodeEntry<RenderModelPage>,
  options: {
    imageMap: ImageMap;
    links: PDFLinks;
  }
) {
  const [{ pageNum: pageNumber }] = rMPageEntry;
  const { imageMap, links } = options;

  const activePageDimensionsPx = Queries.activePageDimensions(editor, 'px');
  const isLandscape = activePageDimensionsPx.width > activePageDimensionsPx.height;
  const orientation = isLandscape ? 'landscape' : 'portrait';
  const pageWidth = activePageDimensionsPx.width;
  const pageHeight = activePageDimensionsPx.height;

  pdf.addPage([pageWidth, pageHeight], orientation);

  const pageBoundingBox: NodeBoundingBox<RenderModelPage> = {
    heightPx: activePageDimensionsPx.height,
    leftPx: 0,
    rMNodeEntry: rMPageEntry,
    topPx: 0,
    type: 'node',
    widthPx: activePageDimensionsPx.width,
  };

  const renderer = new StaticRenderer(editor);
  const unsortedDrawInstructions = renderer.render(pageBoundingBox, {
    ...nullRenderOptions,
    isPDFExport: true,
  });

  const drawInstructions = sortBy(
    drawInstruction => ('zIndex' in drawInstruction ? (drawInstruction.zIndex ?? 0) : 0),
    unsortedDrawInstructions
  );

  const pdfRenderer = new PDFRenderer({ imageMap, links, pageHeight, pageNumber, pdf });

  for (const drawInstruction of drawInstructions) {
    pdfRenderer.draw(drawInstruction);
  }
}
